import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type {
  InvestorType,
  V2AdminGetInvestorPortfolioCompaniesResponses,
  V2AdminListActivityResearchResponses,
  V2AdminListPortfolioResearchResponses,
} from '@workspace/api-backend'
import {
  v2AdminGetInvestorPortfolioCompaniesOptions,
  v2AdminListActivityResearchOptions,
  v2AdminListActivityResearchQueryKey,
  v2AdminListPortfolioResearchOptions,
  v2AdminListPortfolioResearchQueryKey,
  v2AdminTriggerActivityResearchMutation,
  v2AdminTriggerPortfolioResearchMutation,
} from '@workspace/api-backend/react-query'
import { Prose } from '@workspace/seedforge-ui/components/prose'
import { Badge } from '@workspace/ui/components/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { cn } from '@workspace/ui/lib/utils'
import { ChevronRight, Eye, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { AgentSessionsTable } from '../../../features/admin/components/agent-sessions-table'
import { ContactPauseControl } from '../../../features/admin/components/contact-pause-control'
import { InvestorDealFlowInteractionsTab } from '../../../features/admin/components/deal-flow-interactions-tab'
import { InvestorDealFlowStartupsTab } from '../../../features/admin/components/investor-deal-flow-startups-tab'
import { InvestorThesisSection } from '../../../features/admin/components/investor-thesis-section'
import { McpTextSetControl } from '../../../features/admin/components/mcp-text-set-control'
import { ObserversTable } from '../../../features/admin/components/observers-table'
import {
  type AdminInvestorPersonaData,
  type AdminMembershipItem,
  type AdminPersonaResearchRequest,
  useAdminAddInvestorCreditsMutation,
  useAdminDocumentDownloadUrlMutation,
  useAdminInvestorByIdQuery,
  useAdminInvestorCreditsQuery,
  useAdminInvestorMembershipsQuery,
  useAdminInvestorPersonaQuery,
  useAdminTriggerPersonaResearchMutation,
  useAdminUpdateInvestorMutation,
} from '../../../features/admin/hooks/use-admin-api'
import { INVESTOR_TYPE_LABELS } from '../../../features/matches/config/labels'
import {
  DocumentPreviewModal,
  type PreviewDocument,
} from '../../../features/preview/components/document-preview-modal'

export const Route = createFileRoute('/admin/investors/$id')({
  component: AdminInvestorDetailPage,
})

const tabs = [
  { key: 'overview', label: 'Overview' },
  { key: 'users', label: 'Users' },
  { key: 'persona', label: 'Thesis' },
  { key: 'activity-research', label: 'Activity Research' },
  { key: 'portfolio-research', label: 'Portfolio Research' },
  { key: 'credits', label: 'Credits' },
  { key: 'startups', label: 'Startups' },
  { key: 'deal-flow', label: 'Deal flow' },
  { key: 'ai-sessions', label: 'AI sessions' },
  { key: 'observers', label: 'Observers' },
] as const

type Tab = (typeof tabs)[number]['key']

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-1 text-sm font-medium">{children}</div>
    </div>
  )
}

type CreditItem = {
  id: string
  amount: number
  type: string
  note: string | null
  adminUserName: string | null
  createdAt: string | Date
}

const creditColumns: TableColumn<CreditItem>[] = [
  {
    header: 'Amount',
    width: '100px',
    render: (tx) => (
      <span
        className={cn(
          'font-medium tabular-nums',
          tx.amount > 0 ? 'text-green-600' : 'text-red-600'
        )}
      >
        {tx.amount > 0 ? '+' : ''}
        {tx.amount}
      </span>
    ),
  },
  {
    header: 'Type',
    width: '140px',
    render: (tx) => (
      <Badge variant="secondary" size="sm">
        {tx.type}
      </Badge>
    ),
  },
  {
    header: 'Admin',
    width: '140px',
    render: (tx) => <CellText>{tx.adminUserName || '\u2014'}</CellText>,
  },
  {
    header: 'Note',
    render: (tx) => <CellText>{tx.note || '\u2014'}</CellText>,
  },
  {
    header: 'Date',
    width: '200px',
    render: (tx) => <CellText>{formatDate(tx.createdAt)}</CellText>,
  },
]

type ResearchVersion = 'monolith-research.v1' | 'decomposed-research.v1'

const RESEARCH_VERSIONS = [
  { value: 'monolith-research.v1', label: 'Monolith v1' },
  { value: 'decomposed-research.v1', label: 'Decomposed v1' },
] as const satisfies ReadonlyArray<{ value: ResearchVersion; label: string }>

function PersonaTab({
  investorId,
  personaData,
  isLoading,
  onTriggerResearch,
  isTriggeringResearch,
}: {
  investorId: string
  personaData: AdminInvestorPersonaData | undefined
  isLoading: boolean
  onTriggerResearch: (version: ResearchVersion) => void
  isTriggeringResearch: boolean
}) {
  const [researchVersion, setResearchVersion] = useState<ResearchVersion>(
    RESEARCH_VERSIONS[0].value
  )
  const requests = personaData?.researchRequests ?? []
  const personas = personaData?.personas ?? []
  const batchPersonas = personas.filter((p) => p.source?.startsWith('custom.batch'))

  const findPersonaForRequest = (request: AdminPersonaResearchRequest) => {
    return personas.find(
      (p) =>
        !p.source?.startsWith('custom.batch') &&
        new Date(p.createdAt).getTime() >= new Date(request.createdAt).getTime()
    )
  }

  if (isLoading) {
    return <div className="py-10 text-center text-muted-foreground">Loading...</div>
  }

  return (
    <div className="space-y-6">
      <InvestorThesisSection investorId={investorId} />
      {batchPersonas.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Batch Personas ({batchPersonas.length})</h3>
          {batchPersonas.map((persona) => (
            <Collapsible key={persona.id} className="rounded-lg border border-border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors rounded-lg">
                <div className="flex items-center gap-3">
                  <ChevronRight className="size-4 text-muted-foreground transition-transform [[data-state=open]>&]:rotate-90" />
                  <span className="text-sm font-medium">{persona.name}</span>
                  <Badge variant="secondary" size="sm">
                    {persona.source}
                  </Badge>
                </div>
                <span className="text-xs text-muted-foreground">
                  {formatDate(persona.createdAt)}
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="border-t border-border px-4 py-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <InfoCard label="Persona Name">{persona.name}</InfoCard>
                    <InfoCard label="Version">{persona.version}</InfoCard>
                    <InfoCard label="Source">{persona.source || '—'}</InfoCard>
                  </div>
                  {persona.brief && (
                    <div>
                      <p className="mb-1 text-xs font-medium text-muted-foreground">Brief</p>
                      <Prose className="text-sm">{persona.brief}</Prose>
                    </div>
                  )}
                  {persona.description && (
                    <div>
                      <p className="mb-1 text-xs font-medium text-muted-foreground">Description</p>
                      <Prose className="text-sm">{persona.description}</Prose>
                    </div>
                  )}
                  {persona.metricPrompts?.length > 0 && (
                    <div>
                      <p className="mb-2 text-xs font-medium text-muted-foreground">
                        Metric Prompts ({persona.metricPrompts.length})
                      </p>
                      <div className="space-y-1">
                        {persona.metricPrompts.map((mp) => (
                          <Collapsible key={mp.id} className="rounded border border-border">
                            <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-2 text-left">
                              <div className="flex items-center gap-2">
                                <ChevronRight className="size-3.5 text-muted-foreground transition-transform [[data-state=open]>&]:rotate-90" />
                                <span className="text-xs font-medium">{mp.metric}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary" size="sm">
                                  {mp.stage}
                                </Badge>
                                <span className="text-xs text-muted-foreground">w:{mp.weight}</span>
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <div className="border-t border-border px-3 py-2">
                                <Prose className="text-xs">{mp.prompt}</Prose>
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Research Requests ({requests.length})</h3>
          <div className="flex items-center gap-2">
            <select
              value={researchVersion}
              onChange={(e) => setResearchVersion(e.target.value as ResearchVersion)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {RESEARCH_VERSIONS.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
            <CtaButton
              onClick={() => onTriggerResearch(researchVersion)}
              loading={isTriggeringResearch}
            >
              Trigger research
            </CtaButton>
          </div>
        </div>

        {requests.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">No research requests</div>
        ) : (
          <div className="space-y-2">
            {requests.map((r) => {
              const persona = r.status === 'COMPLETED' ? findPersonaForRequest(r) : null

              return (
                <Collapsible key={r.id} className="rounded-lg border border-border bg-card">
                  <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors rounded-lg">
                    <div className="flex items-center gap-3">
                      <ChevronRight className="size-4 text-muted-foreground transition-transform [[data-state=open]>&]:rotate-90" />
                      <Badge
                        variant={
                          r.status === 'COMPLETED'
                            ? 'success'
                            : r.status === 'FAILED'
                              ? 'destructive'
                              : r.status === 'ACTIVE' || r.status === 'PENDING'
                                ? 'warning'
                                : 'secondary'
                        }
                        size="sm"
                      >
                        {r.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{r.version}</span>
                      {persona && <span className="text-sm font-medium">{persona.name}</span>}
                    </div>
                    <span className="text-xs text-muted-foreground">{formatDate(r.createdAt)}</span>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="border-t border-border px-4 py-3 space-y-3">
                      {r.error && (
                        <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
                          {r.error}
                        </div>
                      )}

                      {persona && (
                        <>
                          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                            <InfoCard label="Persona Name">{persona.name}</InfoCard>
                            <InfoCard label="Version">{persona.version}</InfoCard>
                            <InfoCard label="Source">{persona.source || '—'}</InfoCard>
                          </div>

                          {persona.brief && (
                            <div>
                              <p className="mb-1 text-xs font-medium text-muted-foreground">
                                Brief
                              </p>
                              <Prose className="text-sm">{persona.brief}</Prose>
                            </div>
                          )}

                          {persona.description && (
                            <div>
                              <p className="mb-1 text-xs font-medium text-muted-foreground">
                                Description
                              </p>
                              <Prose className="text-sm">{persona.description}</Prose>
                            </div>
                          )}

                          {persona.metricPrompts?.length > 0 && (
                            <div>
                              <p className="mb-2 text-xs font-medium text-muted-foreground">
                                Metric Prompts ({persona.metricPrompts.length})
                              </p>
                              <div className="space-y-1">
                                {persona.metricPrompts.map((mp) => (
                                  <Collapsible key={mp.id} className="rounded border border-border">
                                    <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-2 text-left">
                                      <div className="flex items-center gap-2">
                                        <ChevronRight className="size-3.5 text-muted-foreground transition-transform [[data-state=open]>&]:rotate-90" />
                                        <span className="text-xs font-medium">{mp.metric}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Badge variant="secondary" size="sm">
                                          {mp.stage}
                                        </Badge>
                                        <span className="text-xs text-muted-foreground">
                                          w:{mp.weight}
                                        </span>
                                      </div>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                      <div className="border-t border-border px-3 py-2">
                                        <Prose className="text-xs">{mp.prompt}</Prose>
                                      </div>
                                    </CollapsibleContent>
                                  </Collapsible>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {!persona && r.status === 'COMPLETED' && (
                        <p className="text-xs text-muted-foreground">
                          No persona generated for this request
                        </p>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

type ActivityResearchItem = V2AdminListActivityResearchResponses[200]['data']['items'][number]

const ACTIVITY_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'destructive'> =
  {
    PENDING: 'secondary',
    ENQUEUED: 'warning',
    ACTIVE: 'warning',
    COMPLETED: 'success',
    FAILED: 'destructive',
    CANCELED: 'secondary',
  }

const activityResearchColumns: TableColumn<ActivityResearchItem>[] = [
  {
    header: 'Run ID',
    width: '100px',
    render: (item) => (
      <span className="font-mono text-xs text-muted-foreground">{item.id.slice(0, 8)}</span>
    ),
  },
  {
    header: 'Status',
    width: '120px',
    render: (item) => (
      <Badge variant={ACTIVITY_STATUS_VARIANT[item.status] ?? 'secondary'} size="sm">
        {item.status}
      </Badge>
    ),
  },
  {
    header: 'Error',
    render: (item) =>
      item.error ? (
        <CellText className="max-w-xs truncate text-destructive">{item.error}</CellText>
      ) : (
        <CellText>—</CellText>
      ),
  },
  {
    header: 'Created',
    width: '180px',
    render: (item) => <CellText>{formatDate(item.createdAt)}</CellText>,
  },
  {
    header: 'Updated',
    width: '180px',
    render: (item) => <CellText>{formatDate(item.updatedAt)}</CellText>,
  },
]

function ActivityResearchTab({ investorId }: { investorId: string }) {
  const queryClient = useQueryClient()
  const { data: response, isLoading } = useQuery(
    v2AdminListActivityResearchOptions({ query: { investorId } })
  )
  const triggerMutation = useMutation({
    ...v2AdminTriggerActivityResearchMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListActivityResearchQueryKey() })
    },
  })

  const items: ActivityResearchItem[] = response?.data?.items ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Activity Research Runs ({items.length})</h3>
        <CtaButton
          onClick={() => triggerMutation.mutate({ path: { id: investorId } })}
          loading={triggerMutation.isPending}
        >
          Trigger research
        </CtaButton>
      </div>

      <DataTable
        columns={activityResearchColumns}
        data={items}
        isLoading={isLoading}
        emptyMessage="No activity research runs."
        keyExtractor={(item) => item.id}
      />
    </div>
  )
}

type PortfolioResearchItem = V2AdminListPortfolioResearchResponses[200]['data']['items'][number]

const PORTFOLIO_STATUS_VARIANT: Record<
  string,
  'success' | 'warning' | 'secondary' | 'destructive'
> = {
  PENDING: 'secondary',
  ENQUEUED: 'warning',
  ACTIVE: 'warning',
  COMPLETED: 'success',
  FAILED: 'destructive',
  CANCELED: 'secondary',
}

const portfolioResearchColumns: TableColumn<PortfolioResearchItem>[] = [
  {
    header: 'Run ID',
    width: '100px',
    render: (item) => (
      <span className="font-mono text-xs text-muted-foreground">{item.id.slice(0, 8)}</span>
    ),
  },
  {
    header: 'Status',
    width: '120px',
    render: (item) => (
      <Badge variant={PORTFOLIO_STATUS_VARIANT[item.status] ?? 'secondary'} size="sm">
        {item.status}
      </Badge>
    ),
  },
  {
    header: 'Founders Found',
    width: '140px',
    render: (item) => (
      <CellText>{item.foundersFound != null ? String(item.foundersFound) : '—'}</CellText>
    ),
  },
  {
    header: 'Error',
    render: (item) =>
      item.error ? (
        <CellText className="max-w-xs truncate text-destructive">{item.error}</CellText>
      ) : (
        <CellText>—</CellText>
      ),
  },
  {
    header: 'Created',
    width: '180px',
    render: (item) => <CellText>{formatDate(item.createdAt)}</CellText>,
  },
  {
    header: 'Updated',
    width: '180px',
    render: (item) => <CellText>{formatDate(item.updatedAt)}</CellText>,
  },
]

type PortfolioCompanyItem =
  V2AdminGetInvestorPortfolioCompaniesResponses[200]['data']['companies'][number]

function PortfolioCompaniesSection({ investorId }: { investorId: string }) {
  const { data: response, isLoading } = useQuery(
    v2AdminGetInvestorPortfolioCompaniesOptions({ path: { id: investorId } })
  )
  const companies: PortfolioCompanyItem[] = response?.data?.companies ?? []

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading companies...</p>
  if (!companies.length) return null

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">Portfolio Companies ({companies.length})</h3>
      <div className="space-y-2">
        {companies.map((company) => (
          <div key={company.id} className="rounded-lg border border-border bg-card p-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{company.name}</span>
              {company.linkedinUrl && (
                <a
                  href={company.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  LinkedIn
                </a>
              )}
              {company.crunchbaseUrl && (
                <a
                  href={company.crunchbaseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:underline"
                >
                  Crunchbase
                </a>
              )}
            </div>
            {company.contacts.length > 0 ? (
              <ul className="mt-2 space-y-1">
                {company.contacts.map((contact) => (
                  <li
                    key={contact.id}
                    className="flex items-center gap-2 text-xs text-muted-foreground"
                  >
                    <span className="font-medium text-foreground">{contact.name ?? '—'}</span>
                    {contact.title && <span>· {contact.title}</span>}
                    {contact.linkedinUrl && (
                      <a
                        href={contact.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        LinkedIn
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">No contacts found</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function PortfolioResearchTab({ investorId }: { investorId: string }) {
  const queryClient = useQueryClient()
  const { data: response, isLoading } = useQuery(
    v2AdminListPortfolioResearchOptions({ query: { investorId } })
  )
  const triggerMutation = useMutation({
    ...v2AdminTriggerPortfolioResearchMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListPortfolioResearchQueryKey() })
    },
  })

  const items: PortfolioResearchItem[] = response?.data?.items ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Portfolio Research Runs ({items.length})</h3>
        <CtaButton
          onClick={() => triggerMutation.mutate({ path: { id: investorId } })}
          loading={triggerMutation.isPending}
        >
          Trigger research
        </CtaButton>
      </div>

      <DataTable
        columns={portfolioResearchColumns}
        data={items}
        isLoading={isLoading}
        emptyMessage="No portfolio research runs."
        keyExtractor={(item) => item.id}
      />
      <PortfolioCompaniesSection investorId={investorId} />
    </div>
  )
}

function AdminInvestorDetailPage() {
  const { id } = Route.useParams()
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [creditAmount, setCreditAmount] = useState('')
  const [creditNote, setCreditNote] = useState('')
  const [pollEnabled, setPollEnabled] = useState(false)
  const [previewDoc, setPreviewDoc] = useState<PreviewDocument | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const docDownloadMutation = useAdminDocumentDownloadUrlMutation()

  const investorQuery = useAdminInvestorByIdQuery(id)
  const creditsQuery = useAdminInvestorCreditsQuery(id)
  const personaQuery = useAdminInvestorPersonaQuery(id)
  const addCreditsMutation = useAdminAddInvestorCreditsMutation(id)
  const updateInvestorMutation = useAdminUpdateInvestorMutation(id)
  const triggerResearchMutation = useAdminTriggerPersonaResearchMutation(id)

  const personaData = personaQuery.data?.data as AdminInvestorPersonaData | undefined

  useEffect(() => {
    const hasActive =
      personaData?.researchRequests?.some(
        (r: { status: string }) => r.status === 'PENDING' || r.status === 'ACTIVE'
      ) ?? false
    setPollEnabled(hasActive)
  }, [personaData])

  // Poll when research is in progress
  useEffect(() => {
    if (!pollEnabled) return
    const interval = setInterval(() => {
      personaQuery.refetch()
    }, 5000)
    return () => clearInterval(interval)
  }, [pollEnabled, personaQuery.refetch])

  const investor = investorQuery.data?.data
  const credits = creditsQuery.data?.data

  if (investorQuery.isLoading) {
    return <div className="py-12 text-center text-muted-foreground">Loading...</div>
  }

  if (!investor) {
    return <div className="py-12 text-center text-muted-foreground">Investor not found.</div>
  }

  const handleAddCredits = async (e: React.FormEvent) => {
    e.preventDefault()
    const amount = Number.parseInt(creditAmount, 10)
    if (!amount || amount <= 0) return
    await addCreditsMutation.mutateAsync({
      path: { id },
      body: { amount, note: creditNote || undefined },
    })
    setCreditAmount('')
    setCreditNote('')
  }

  const handleTriggerResearch = async (version: ResearchVersion) => {
    await triggerResearchMutation.mutateAsync({
      path: { id },
      body: { version },
    })
  }

  return (
    <>
      <nav className="border-b border-border">
        <div className="-mb-px flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                activeTab === tab.key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>
      <div className="mt-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Logo */}
            {investor.logoUrl && (
              <div className="flex items-center gap-4">
                <img
                  src={investor.logoUrl}
                  alt={investor.name}
                  className="size-16 rounded-lg border border-border object-cover"
                />
              </div>
            )}

            {/* Core Fields */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <InfoCard label="Name">{investor.name}</InfoCard>
              <InfoCard label="Slug">{investor.slug || '\u2014'}</InfoCard>
              {investor.websiteUrl && (
                <InfoCard label="Website">
                  <a
                    href={investor.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {investor.websiteUrl}
                  </a>
                </InfoCard>
              )}
              {investor.linkedinUrl && (
                <InfoCard label="LinkedIn">
                  <a
                    href={investor.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline truncate block"
                  >
                    {investor.linkedinUrl}
                  </a>
                </InfoCard>
              )}
              <InfoCard label="Accepting Pitches">
                <Badge variant={investor.isAcceptingPitches ? 'success' : 'secondary'} size="sm">
                  {investor.isAcceptingPitches ? 'Yes' : 'No'}
                </Badge>
              </InfoCard>
              <InfoCard label="Type">
                <select
                  value={investor.investorType ?? ''}
                  onChange={(e) =>
                    updateInvestorMutation.mutate({
                      path: { id },
                      body: { investorType: (e.target.value || null) as InvestorType | null },
                    })
                  }
                  disabled={updateInvestorMutation.isPending}
                  className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm disabled:opacity-50"
                >
                  <option value="">{'— Unset'}</option>
                  {Object.entries(INVESTOR_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </InfoCard>
              <InfoCard label="Outreach Status">
                {investor.inactiveUntil && new Date(investor.inactiveUntil) > new Date() ? (
                  <div className="flex flex-col gap-1">
                    <Badge variant="warning" size="sm">
                      Paused until{' '}
                      {new Date(investor.inactiveUntil).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Badge>
                    {investor.inactiveReason && (
                      <span className="text-xs text-muted-foreground">
                        {investor.inactiveReason}
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={updateInvestorMutation.isPending}
                      onClick={() =>
                        updateInvestorMutation.mutate({
                          path: { id },
                          body: {
                            investorType: investor.investorType,
                            inactiveUntil: null,
                            inactiveReason: null,
                          },
                        })
                      }
                      className="self-start text-xs text-primary hover:underline disabled:opacity-50"
                    >
                      Resume now
                    </button>
                  </div>
                ) : (
                  <Badge variant="success" size="sm">
                    Active
                  </Badge>
                )}
              </InfoCard>
              <InfoCard label="Persona Status">
                <Badge
                  variant={
                    investor.personaStatus === 'ready'
                      ? 'success'
                      : investor.personaStatus === 'failed'
                        ? 'destructive'
                        : investor.personaStatus === 'processing'
                          ? 'default'
                          : 'secondary'
                  }
                  size="sm"
                >
                  {investor.personaStatus}
                </Badge>
              </InfoCard>
            </div>

            {/* Contact GPs + Deal Info */}
            {(investor.contacts.length > 0 || investor.lastDealAt) && (
              <div className="rounded-lg border border-border bg-card px-4 py-3 space-y-2">
                <p className="text-xs text-muted-foreground">Contact &amp; Deal Info</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 mt-1">
                  {investor.contacts.map((contact) => (
                    <InfoCard key={contact.id} label={contact.title ?? 'GP'}>
                      <span
                        className={`font-medium ${contact.excludedAt ? 'text-muted-foreground line-through' : ''}`}
                      >
                        {contact.name}
                      </span>
                      {contact.excludedAt && (
                        <Badge variant="destructive" size="sm" className="ml-1">
                          excluded
                        </Badge>
                      )}
                      {contact.scope && (
                        <span className="ml-1 text-muted-foreground text-xs">
                          · {contact.scope}
                        </span>
                      )}
                      {contact.linkedinUrl && (
                        <a
                          href={contact.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-primary hover:underline truncate text-xs mt-0.5"
                        >
                          {contact.linkedinUrl}
                        </a>
                      )}
                      <ContactPauseControl
                        contactId={contact.id}
                        pausedUntil={contact.pausedUntil}
                        pausedReason={contact.pausedReason}
                        isExcluded={Boolean(contact.excludedAt)}
                      />
                    </InfoCard>
                  ))}
                  {investor.lastDealAt && (
                    <InfoCard label="Last Deal At">
                      {new Date(investor.lastDealAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </InfoCard>
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            {investor.description && (
              <div className="rounded-lg border border-border bg-card px-4 py-3">
                <p className="text-xs text-muted-foreground">Description</p>
                <div className="mt-1">
                  <Prose>{investor.description}</Prose>
                </div>
              </div>
            )}

            {/* Investment Areas & Geographies */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InfoCard label="Investment Areas">
                {investor.investmentAreas && investor.investmentAreas.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {investor.investmentAreas.map((area: string) => (
                      <Badge key={area} variant="secondary" size="sm">
                        {area}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  '-'
                )}
              </InfoCard>
              <InfoCard label="Investment Geographies">
                {investor.investmentGeographies && investor.investmentGeographies.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {investor.investmentGeographies.map((geo: string) => (
                      <Badge key={geo} variant="secondary" size="sm">
                        {geo}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  '-'
                )}
              </InfoCard>
            </div>

            {/* Kill Criteria */}
            {investor.killCriteria && (
              <div className="rounded-lg border border-border bg-card px-4 py-3">
                <p className="text-xs text-muted-foreground">Kill Criteria</p>
                <div className="mt-1">
                  <Prose>{investor.killCriteria}</Prose>
                </div>
              </div>
            )}

            {/* Ingested Persona */}
            {investor.ingestedPersona && (
              <div className="rounded-lg border border-border bg-card px-4 py-3">
                <p className="text-xs text-muted-foreground">Ingested Persona</p>
                <div className="mt-1">
                  <Prose>{investor.ingestedPersona}</Prose>
                </div>
              </div>
            )}

            {/* Thesis Document */}
            {investor.thesisDocument && (
              <InfoCard label="Thesis Document">
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-primary hover:underline"
                  onClick={() => {
                    const doc = investor.thesisDocument
                    if (!doc) return
                    setPreviewDoc({
                      id: doc.id,
                      fileName: doc.fileName,
                      mimeType: doc.mimeType ?? null,
                    })
                    setPreviewOpen(true)
                  }}
                >
                  <Eye className="size-3.5" />
                  {investor.thesisDocument.fileName || investor.thesisDocument.id}
                </button>
              </InfoCard>
            )}

            {/* Cover Image */}
            {investor.coverImageUrl && (
              <div className="rounded-lg border border-border bg-card px-4 py-3">
                <p className="text-xs text-muted-foreground">Cover Image</p>
                <div className="mt-2">
                  <img
                    src={investor.coverImageUrl}
                    alt="Cover"
                    className="max-h-48 rounded-md border border-border object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && <InvestorUsersTab investorId={id} />}

        {activeTab === 'persona' && (
          <PersonaTab
            investorId={id}
            personaData={personaData}
            isLoading={personaQuery.isLoading}
            onTriggerResearch={handleTriggerResearch}
            isTriggeringResearch={triggerResearchMutation.isPending}
          />
        )}

        {activeTab === 'activity-research' && <ActivityResearchTab investorId={id} />}
        {activeTab === 'portfolio-research' && <PortfolioResearchTab investorId={id} />}

        {activeTab === 'credits' && (
          <div className="space-y-6">
            <div className="text-sm text-muted-foreground">
              Balance:{' '}
              <span className="font-medium tabular-nums text-foreground">
                {credits?.balance ?? '\u2014'}
              </span>
            </div>
            <DataTable
              columns={creditColumns}
              data={(credits?.transactions as CreditItem[]) ?? []}
              isLoading={creditsQuery.isLoading}
              emptyMessage="No credit transactions."
              keyExtractor={(tx) => tx.id}
            />
            <form onSubmit={handleAddCredits} className="flex items-end gap-3">
              <div>
                <label htmlFor="credit-amount" className="mb-1 block text-sm font-medium">
                  Amount
                </label>
                <input
                  id="credit-amount"
                  type="number"
                  min="1"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  className="h-9 w-24 rounded-md border border-input bg-background px-3 text-sm tabular-nums"
                  required
                />
              </div>
              <div>
                <label htmlFor="credit-note" className="mb-1 block text-sm font-medium">
                  Note
                </label>
                <input
                  id="credit-note"
                  type="text"
                  value={creditNote}
                  onChange={(e) => setCreditNote(e.target.value)}
                  className="h-9 w-64 rounded-md border border-input bg-background px-3 text-sm"
                  placeholder="Optional note"
                />
              </div>
              <CtaButton type="submit" loading={addCreditsMutation.isPending}>
                Add credits
              </CtaButton>
            </form>
          </div>
        )}

        {activeTab === 'startups' && <InvestorDealFlowStartupsTab investorId={id} />}

        {activeTab === 'deal-flow' && <InvestorDealFlowInteractionsTab investorId={id} />}

        {activeTab === 'ai-sessions' && <AgentSessionsTable investorId={id} />}

        {activeTab === 'observers' && <ObserversTable investorId={id} showSummary={false} />}
      </div>
      <DocumentPreviewModal
        document={previewDoc}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        fetchUrl={async (docId) => {
          const res = await docDownloadMutation.mutateAsync({ path: { id: docId } })
          const data = res as { data?: { downloadUrl?: string } }
          return data.data?.downloadUrl ?? ''
        }}
      />
    </>
  )
}

// ─── Investor Users Tab (memberships - people managing) ──────────────────────

function InvestorUsersTab({ investorId }: { investorId: string }) {
  const membershipsQuery = useAdminInvestorMembershipsQuery(investorId)
  const members = (membershipsQuery.data?.data ?? []) as AdminMembershipItem[]

  if (membershipsQuery.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (members.length === 0) {
    return <div className="py-6 text-center text-sm text-muted-foreground">No members</div>
  }

  return (
    <div className="space-y-2">
      {members.map((m, i) => (
        <div
          key={m.userId ?? i}
          className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3"
        >
          <div>
            <p className="text-sm font-medium">{m.name || m.email || '\u2014'}</p>
            {m.email && <p className="text-xs text-muted-foreground">{m.email}</p>}
            {m.position && <p className="text-xs text-muted-foreground">{m.position}</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge variant="secondary" size="sm">
              {m.role}
            </Badge>
            {m.userId && <McpTextSetControl userId={m.userId} textSet={m.mcpTextSet ?? null} />}
          </div>
        </div>
      ))}
    </div>
  )
}
